import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Divider
} from '@mui/material';
// Placeholder for Solar Flares icon
// Replace with actual import when implemented
import WbSunnyIcon from '@mui/icons-material/WbSunny'; // TEMP: visually similar to solar flares

// Dummy PLANETS array, replace with actual import if available
const PLANETS = [
  'Sun', 'Moon', 'Mercury', 'Venus', 'Mars',
  'Jupiter', 'Saturn', 'Rahu', 'Ketu'
];

const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta',
  'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];
const NAKSHATRA_COLORS = [
  '#FFC300', '#FF5733', '#C70039', '#900C3F', '#581845', '#DAF7A6', '#FFC300', '#FF5733', '#C70039',
  '#900C3F', '#581845', '#DAF7A6', '#FFC300', '#FF5733', '#C70039', '#900C3F', '#581845', '#DAF7A6',
  '#FFC333', '#FF5733', '#C70039', '#900C3F', '#581845', '#DAF7A6', '#FFC300', '#FF5733', '#C70039', '#900C3F'
];

import { useChartOverlays } from '../context/ChartOverlayContext';
import { useAuth } from '../context/AuthContext';
import { createOverlaysForDatePair } from '../utils/overlayUtils';

// Helper to get overlay data source (always use extendedBarData if present)
function getOverlayData(barData, extendedBarData) {
  return (extendedBarData && extendedBarData.dates && extendedBarData.dates.length > 0) ? extendedBarData : barData;
}
// Zodiac sign names for overlays
const SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const SIGN_COLORS = [
  '#e6194b', // Aries - red
  '#3cb44b', // Taurus - green
  '#ffe119', // Gemini - yellow
  '#4363d8', // Cancer - blue
  '#f58231', // Leo - orange
  '#911eb4', // Virgo - purple
  '#46f0f0', // Libra - cyan
  '#f032e6', // Scorpio - magenta
  '#bcf60c', // Sagittarius - lime
  '#fabebe', // Capricorn - pink
  '#008080', // Aquarius - teal
  '#e6beff'  // Pisces - lavender
];


const AggregateAreaHighlightDialog = ({ open, onClose, barData, extendedBarData }) => {
  const { addOverlay } = useChartOverlays();
  const { api } = useAuth();
  // Row 1: Planet in All Signs
  const [planetAllSigns, setPlanetAllSigns] = useState('Mars');
  // Row 2: Planet in All Nakshatras
  const [planetAllNakshatras, setPlanetAllNakshatras] = useState('Mars');


  

  // Row 1: handler
  const handleRunAllSigns = async () => {
    if (!barData || !barData.dates || barData.dates.length < 2) {
      console.error('[AggregateAreaHighlightDialog] No barData or insufficient dates');
      return;
    }
    const payload = {
      planet: planetAllSigns,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1],
      zodiac: 'sidereal'
    };
    console.log('[AggregateAreaHighlightDialog] RUN parameters:', {
      endpoint: '/api/area-highlight-study/planet-in-all-signs',
      payload
    });
    try {
      const response = await api.post('/api/area-highlight-study/planet-in-all-signs', payload);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      console.log(`[AggregateAreaHighlightDialog] Raw data for 'Planet in All Signs':`, JSON.stringify(data, null, 2));
      if (Array.isArray(data) && data.length > 0) {
        // Robust: support [{sign, datePairs}] or [ [pairs...] ]
        const overlays = [];
        const overlayData = getOverlayData(barData, extendedBarData);
        data.forEach((pairs, idx) => {
          let signIdx = idx, signName = SIGNS[idx], overlayColor = SIGN_COLORS[idx % SIGN_COLORS.length];
          // If backend returns {sign, datePairs}
          if (pairs && pairs.sign && Array.isArray(pairs.datePairs)) {
            signName = pairs.sign;
            signIdx = SIGNS.indexOf(signName);
            overlayColor = SIGN_COLORS[signIdx >= 0 ? signIdx : idx % SIGN_COLORS.length];
            pairs = pairs.datePairs;
          }
          if (Array.isArray(pairs)) {
            pairs.forEach(pair => {
              const overlaysForPair = createOverlaysForDatePair(
                pair,
                overlayColor,
                `${planetAllSigns} in ${signName}`,
                overlayData,
                { planet1: planetAllSigns, sign: signName, extendedDates: overlayData.dates }
              );
              overlays.push(...overlaysForPair);
            });
          }
        });
        console.log('[AggregateAreaHighlightDialog] overlays to add:', overlays);
        if (overlays.length > 0) {
          addOverlay(overlays);
          console.log(`[AggregateAreaHighlightDialog] Added ${overlays.length} overlays`);
        } else {
          console.log('[AggregateAreaHighlightDialog] No overlays to add');
        }
      } else {
        console.log(`[AggregateAreaHighlightDialog] Study 'Planet in All Signs' returned no data.`);
      }
    } catch (error) {
      console.error(`[AggregateAreaHighlightDialog] Failed to run study 'Planet in All Signs':`, error);
    }
  };

  // Row 2: handler
  const handleRunAllNakshatras = async () => {
    if (!barData || !barData.dates || barData.dates.length < 2) {
      console.error('[AggregateAreaHighlightDialog] No barData or insufficient dates');
      return;
    }
    const payload = {
      planet: planetAllNakshatras,
      startDateTime: barData.dates[0],
      endDateTime: barData.dates[barData.dates.length - 1]
    };
    console.log('[AggregateAreaHighlightDialog] RUN parameters:', {
      endpoint: '/api/area-highlight-study/planet-in-all-nakshatras',
      payload
    });
    try {
      const response = await api.post('/api/area-highlight-study/planet-in-all-nakshatras', payload);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      console.log(`[AggregateAreaHighlightDialog] Raw data for 'Planet in All Nakshatras':`, JSON.stringify(data, null, 2));
      if (Array.isArray(data) && data.length > 0) {
        const overlays = [];
        const overlayData = getOverlayData(barData, extendedBarData);
        data.forEach((pairs, idx) => {
          let nakIdx = idx, nakName = NAKSHATRAS[idx], overlayColor = NAKSHATRA_COLORS[idx % NAKSHATRA_COLORS.length];
          // If backend returns {nakshatra, datePairs}
          if (pairs && pairs.nakshatra && Array.isArray(pairs.datePairs)) {
            nakName = pairs.nakshatra;
            nakIdx = NAKSHATRAS.indexOf(nakName);
            overlayColor = NAKSHATRA_COLORS[nakIdx >= 0 ? nakIdx : idx % NAKSHATRA_COLORS.length];
            pairs = pairs.datePairs;
          }
          if (Array.isArray(pairs)) {
            pairs.forEach(pair => {
              const overlaysForPair = createOverlaysForDatePair(
                pair,
                overlayColor,
                `${planetAllNakshatras} in ${nakName}`,
                overlayData,
                { planet1: planetAllNakshatras, nakshatra: nakName, extendedDates: overlayData.dates }
              );
              overlays.push(...overlaysForPair);
            });
          }
        });
        console.log('[AggregateAreaHighlightDialog] overlays to add:', overlays);
        if (overlays.length > 0) {
          addOverlay(overlays);
          console.log(`[AggregateAreaHighlightDialog] Added ${overlays.length} overlays`);
        } else {
          console.log('[AggregateAreaHighlightDialog] No overlays to add');
        }
      } else {
        console.log(`[AggregateAreaHighlightDialog] Study 'Planet in All Nakshatras' returned no data.`);
      }
    } catch (error) {
      console.error(`[AggregateAreaHighlightDialog] Failed to run study 'Planet in All Nakshatras':`, error);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Solar Flares Icon Placeholder */}
        <WbSunnyIcon sx={{ color: '#f9a825', fontSize: 32 }} />
        <Typography variant="h6" component="span" sx={{ fontWeight: 'bold', flex: 1 }}>
          Aggregate Area Highlight Studies
        </Typography>
      </DialogTitle>
      <DialogContent>
        {/* Row 1: Planet In All Signs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, border: '1px solid #ddd', borderRadius: 2, mb: 2, background: '#fff' }}>
          <Typography sx={{ minWidth: 60, fontWeight: 500 }}>Planet</Typography>
          <Select
            value={planetAllSigns}
            onChange={e => setPlanetAllSigns(e.target.value)}
            size="small"
            sx={{ minWidth: 100 }}
          >
            {PLANETS.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontWeight: 500, ml: 1 }}>
            In All Signs
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleRunAllSigns}
            sx={{ minWidth: 64 }}
          >
            RUN
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />
        {/* Row 2: Planet In All Nakshatras */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, border: '1px solid #ddd', borderRadius: 2, background: '#fff' }}>
          <Typography sx={{ minWidth: 60, fontWeight: 500 }}>Planet</Typography>
          <Select
            value={planetAllNakshatras}
            onChange={e => setPlanetAllNakshatras(e.target.value)}
            size="small"
            sx={{ minWidth: 100 }}
          >
            {PLANETS.map(p => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontWeight: 500, ml: 1 }}>
            In All Nakshatras
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleRunAllNakshatras}
            sx={{ minWidth: 64 }}
          >
            RUN
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
          <Button
            onClick={onClose}
            variant="contained"
            color="error"
          >
            CLOSE
          </Button>
      </DialogActions>
    </Dialog>
  );
};


AggregateAreaHighlightDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string)
  }),
  extendedBarData: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.string),
    ohlc: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
};

export default AggregateAreaHighlightDialog;
